import { Component, HostListener, Injectable, OnInit, ViewEncapsulation } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { XpoSnackBar } from '@xpo-ltl/ngx-ltl-core/snack-bar';
import {
  AppName,
  PickupFormTypeEnum,
  PickupRequestData,
  XpoLtlPickupRequestComponentConfig,
} from '@xpo-ltl/ngx-ltl-pickup-request';
import { combineLatest, of } from 'rxjs';
import { IframeMessage, postIframeMessage } from '../shared/iframe-message';

const NAVIGATION_DELAY_MS = 500;

// Hack to capture the last snackbar params passed in the open function.
@Injectable()
export class PickupRequestSnackbar extends XpoSnackBar {
  lastParams: any;
  override open(params: any): MatSnackBarRef<any> {
    this.lastParams = params;
    return super.open(params);
  }
}

@Component({
  selector: 'app-pickup-request',
  templateUrl: './pickup-request.component.html',
  styleUrls: ['./pickup-request.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'opstms-PickupRequest' },
})
export class PickupRequestComponent implements OnInit {
  pickupRequestComponentConfig: XpoLtlPickupRequestComponentConfig | null;
  pickupFormType: PickupFormTypeEnum;
  pickupData: PickupRequestData;

  pickupRequestId: string;
  callNbr: string;
  proNbr: string;
  isCreateMode: boolean;
  isShipmentCancellationRestricted: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private pickupRequestSnackbar: PickupRequestSnackbar
  ) {}

  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    const message = event.data as IframeMessage;
    if (!message?.event) return;
    this.handlePickupRequestEvent(message);
  }

  private handlePickupRequestEvent(message: IframeMessage): boolean {
    //TODO: Reschedule/Cancel, clone
    if (message.event === 'submit' && message.data?.pickupFormType === PickupFormTypeEnum.UPDATE_PICKUP) {
      const pickupRequestResponse = message.data?.callbackResponse?.pickupRequestResponse;
      const pickupRequestId = pickupRequestResponse?.pkrPickupRequestId;
      const callNbr = pickupRequestResponse?.callNbr;
      if (pickupRequestId != null && callNbr != null) {
        this.pickupRequestComponentConfig = null;
        // Update Pickup from edit-pickup does not trigger the close callback,
        // so we need to reset the snackbar info here to avoid showing the old snackbar info on next close.
        // This is a hack since we dont want to touch the PUR component now,
        // real fix would be to have the component call the callback function on close and reset the snackbar info there.
        this.pickupRequestSnackbar.lastParams = undefined;
        this.navigateToEditPickup(pickupRequestId, callNbr);
        return true;
      }
    }

    if (message.event === 'submit' && message.data?.pickupFormType === PickupFormTypeEnum.ADD_PICKUP) {
      const pkrPickupRequest = message.data?.callbackResponse?.completePickupRequests?.[0]?.pkrPickupRequest;
      if (message.data?.noramlCreate) {
        const pickupRequestId = pkrPickupRequest?.pkrPickupRequestId;
        const callNbr = pkrPickupRequest?.callNbr;
        if (pickupRequestId != null && callNbr != null) {
          this.pickupRequestComponentConfig = null;
          this.navigateToEditPickup(pickupRequestId, callNbr);
          return true;
        }
      } else {
        const customerNbr = pkrPickupRequest?.shipperCisCustomerNbr;
        const sicCd = pkrPickupRequest?.shipperSicCd;
        if (customerNbr != null) {
          this.isCreateMode = true;
          this.pickupFormType = PickupFormTypeEnum.ADD_PICKUP;
          this.pickupData = { pickupIdCallsNbrList: [], customerNbr, sicCd };
          this.pickupRequestComponentConfig = null;
          this.initializePickupRequestComponentConfig();
          this.router.navigate(['/create-pickup'], { queryParams: { customerNbr, sicCd } });
          return true;
        }
      }
    }

    return false;
  }

  ngOnInit() {
    combineLatest([this.route.params, this.route.queryParams]).subscribe(([params, queryParams]) => {
      const currentRoute = this.route.snapshot.routeConfig?.path;
      if (currentRoute === 'create-pickup') {
        this.isCreateMode = true;
        this.pickupFormType = PickupFormTypeEnum.ADD_PICKUP;
        this.pickupData = {
          sicCd: queryParams['sicCd'],
          customerNbr: queryParams['customerNbr'],
          pickupIdCallsNbrList: [],
        };
      } else if (currentRoute === 'update-pickups') {
        if (!queryParams['pickupRequestIds'] || !queryParams['callNbrs']) {
          console.error('Missing pickupRequestIds or callNbrs in query parameters');
          return;
        }
        const pickupRequestIds = queryParams['pickupRequestIds']?.split(',') || [];
        const callNbrs = queryParams['callNbrs']?.split(',') || [];

        if (pickupRequestIds.length !== callNbrs.length) {
          console.error('pickupRequestIds and callNbrs length mismatch');
          return;
        }
        this.isShipmentCancellationRestricted = true;
        this.isCreateMode = false;
        this.pickupFormType = PickupFormTypeEnum.RESCHEDULE_OR_CANCEL;

        const pickupIdCallsNbrList = pickupRequestIds.map((id: string, index: number) => ({
          id: +id,
          callNbr: callNbrs[index] || '',
        }));

        this.pickupData = {
          pickupIdCallsNbrList,
          // PUR component requires appName to be set to PND to expand the Reschedule options by default. OPD-1851
          appName: AppName.PND,
        };
      } else if (currentRoute === 'edit-pickup/:pickupRequestId/:callNbr') {
        this.isCreateMode = true;
        this.pickupFormType = PickupFormTypeEnum.UPDATE_PICKUP;
        this.pickupRequestId = params['pickupRequestId'];
        this.callNbr = params['callNbr'];
        this.proNbr = params['proNbr'];

        this.pickupData = {
          pickupIdCallsNbrList: [
            {
              id: +this.pickupRequestId,
              callNbr: this.callNbr,
              proNbr: this.proNbr,
            },
          ],
        };
      }

      this.initializePickupRequestComponentConfig();
    });
  }

  private navigateToEditPickup(pickupRequestId: string, callNbr: string): void {
    setTimeout(() => {
      this.isCreateMode = true;
      this.pickupFormType = PickupFormTypeEnum.UPDATE_PICKUP;
      this.pickupData = { pickupIdCallsNbrList: [{ id: +pickupRequestId, callNbr }] };
      this.initializePickupRequestComponentConfig();
      this.router.navigate(['/edit-pickup', pickupRequestId, callNbr]);
    }, NAVIGATION_DELAY_MS);
  }

  // The Cancel Pickup success snackbar reads "<callNbr> Cancelled"; the PUR exposes no other success signal to the host.
  private isPickupCancelled(snackbarInfo: any): boolean {
    return (
      this.pickupFormType === PickupFormTypeEnum.UPDATE_PICKUP &&
      typeof snackbarInfo?.message === 'string' &&
      snackbarInfo.message.endsWith(' Cancelled')
    );
  }

  private getLatestSnackBarInfo(): any {
    return this.pickupRequestSnackbar.lastParams;
  }

  initializePickupRequestComponentConfig() {
    this.pickupRequestComponentConfig = {
      headerActions: [
        {
          tooltip: '',
          dataTestAttr: 'pickupRequestActionDefault',
          iconName: ' ',
          disabled$: of(true),
          defaultPosition: true,
          callbackFn: () => {},
        },
      ],
      closeAction: {
        tooltip: 'Close',
        dataTestAttr: 'pickupRequestActionClose',
        iconName: 'close',
        disabled$: of(false),
        callbackFn: (param?: any) => {
          // setTimeout ensures the snackbar info is captured before sending the message, hack since this runs before teh open function.
          // real fix would be to have the component call the callback function on close, but we dont want to touch that component now.
          setTimeout(() => {
            const snackbarInfo = this.getLatestSnackBarInfo();
            // A successful Cancel Pickup runs this callback too; it is sent as a close carrying the cancel toast and a marker.
            const cancelled = this.isPickupCancelled(snackbarInfo);

            const message: IframeMessage = {
              event: 'close',
            };

            if (param || snackbarInfo || cancelled) {
              message.data = {
                ...param,
                ...(snackbarInfo && { snackbar: snackbarInfo }),
                ...(cancelled ? { pickupCancelled: true } : {}),
              };
            }

            this.pickupRequestSnackbar.lastParams = undefined;
            postIframeMessage(message);
          }, 0);
        },
      },

      pickupFormType: this.pickupFormType,
      data: this.pickupData,
      isCreateRestricted: !this.isCreateMode,
      isUpdateRestricted: false,
      isShipmentCancellationRestricted: this.isShipmentCancellationRestricted,

      output: (param: any) => {
        // Form Submit action
        const submitMessage: IframeMessage = { event: 'submit', data: param };
        const handledInternally = this.handlePickupRequestEvent(submitMessage);
        if (!handledInternally) {
          postIframeMessage(submitMessage);
        }
        if (param && param?.success) {
          postIframeMessage({ event: 'pickupUpdated' as any });
        }
      },
    };
  }
}
